import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { RangeType, useAsu } from "@framework/provider-asu";

import { ForecastingContent } from "./content";
import { ForecastingSummaryPanel } from "./summary-panel";
import {
  buildForecastTimelineEndTimestamp,
  DEFAULT_FORECAST_EXTENSION_DAYS,
  MAX_FORECAST_EXTENSION_DAYS,
  normalizeForecastExtensionDays,
} from "./utils";

const FORECAST_EXTENSION_INPUT_DELAY_MS = 400;

export const Forecasting: FC = () => {
  const { endTimestamp, isLoading, userSelectedRangeType } = useAsu();
  const { formatMessage } = useIntl();
  const [isTrainTciTrendPinned, setIsTrainTciTrendPinned] = useState(true);
  const [isForecastExtended, setIsForecastExtended] = useState(false);
  const [forecastExtensionDays, setForecastExtensionDays] = useState(DEFAULT_FORECAST_EXTENSION_DAYS);
  const [forecastExtensionDaysInput, setForecastExtensionDaysInput] = useState(String(DEFAULT_FORECAST_EXTENSION_DAYS));
  const timelineEndTimestamp =
    buildForecastTimelineEndTimestamp(endTimestamp, isForecastExtended ? forecastExtensionDays : 0) ?? endTimestamp;

  useEffect(() => {
    if (forecastExtensionDaysInput.trim() === "") {
      return;
    }

    const value = Number(forecastExtensionDaysInput);

    if (!Number.isFinite(value)) {
      return;
    }

    const timeout = setTimeout(
      () => setForecastExtensionDays(normalizeForecastExtensionDays(value)),
      FORECAST_EXTENSION_INPUT_DELAY_MS,
    );

    return () => clearTimeout(timeout);
  }, [forecastExtensionDaysInput]);

  const commitForecastExtensionDays = () => {
    const value = normalizeForecastExtensionDays(Number(forecastExtensionDaysInput));

    setForecastExtensionDays(value);
    setForecastExtensionDaysInput(String(value));
  };

  return (
    <ProgressOverlay
      isLoading={isLoading && userSelectedRangeType !== RangeType.RECORDER_MODE}
      wrapperSx={{ height: "100%", minHeight: 0 }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ minHeight: 44, px: 2, py: 0.5, borderBottom: "1px solid #e4e8f1", backgroundColor: "#ffffff" }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isForecastExtended}
                onChange={event => setIsForecastExtended(event.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: 13, color: "#363846" }}>
                <FormattedMessage id="pages.forecasting.extendTrends" />
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          <TextField
            disabled={!isForecastExtended}
            inputProps={{
              "aria-label": formatMessage({ id: "pages.forecasting.extensionDaysAriaLabel" }),
              max: MAX_FORECAST_EXTENSION_DAYS,
              min: 1,
              step: 1,
            }}
            onBlur={commitForecastExtensionDays}
            onChange={event => setForecastExtensionDaysInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") {
                commitForecastExtensionDays();
                event.currentTarget.blur();
              }
            }}
            size="small"
            sx={{ width: 72, "& .MuiInputBase-input": { py: 0.75 } }}
            type="number"
            value={forecastExtensionDaysInput}
          />
          <Typography sx={{ fontSize: 13, color: "#363846" }}>
            <FormattedMessage id="pages.forecasting.extensionDays" />
          </Typography>
        </Stack>
        <ForecastingSummaryPanel
          isTrainTciTrendPinned={isTrainTciTrendPinned}
          onTrainTciTrendPinnedChange={setIsTrainTciTrendPinned}
        />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ForecastingContent
            extensionBoundaryTimestamp={isForecastExtended ? endTimestamp : null}
            isTrainTciTrendPinned={isTrainTciTrendPinned}
            timelineEndTimestamp={timelineEndTimestamp}
          />
        </Box>
      </Box>
    </ProgressOverlay>
  );
};
